/**
 * MentorKit LMS Pro Settings — konfigurasjon per medlemsorganisasjon
 *
 * Kompakt versjon vs Moodle/TutorLMS/LearnDash-varianter:
 *   - Webhook-basert (ingen sync-intervall å konfigurere)
 *   - Genererer webhook-URL + secret som medlemsorg limer inn i MentorKit-admin
 *   - Test-tilkobling pinger public scorm_package-endepunkt (ingen auth)
 *   - Application Password er ikke lenger nødvendig (bekreftet av Sindre
 *     Fjellestad, Norsk Interaktiv, 2026-10-01). Brukerfelt (mobil,
 *     fødselsdato) kommer i webhook-payload som data.user.fields.mobil og
 *     data.user.fields.fodselsdato.
 */
import { useState, useEffect } from 'react';
import { authenticatedFetch } from '@/lib/auth';
import { CheckCircle2, AlertCircle, Copy, Loader2, RefreshCw, Trash2, Info, ExternalLink, Zap } from 'lucide-react';

interface Props {
  memberId: string;
}

interface Integration {
  id: string;
  wordpress_url: string;
  client_id: string | null;
  has_client_secret: boolean;
  has_webhook_secret: boolean;
  webhook_url: string | null;
  webhook_url_slug: string | null;
  webhooks_enabled: boolean;
  connection_tested: boolean;
  connection_tested_at: string | null;
  connection_test_error: string | null;
  last_webhook_at: string | null;
  last_webhook_status: string | null;
  last_webhook_error: string | null;
  total_webhooks_received: number;
  total_webhooks_accepted: number;
  total_webhooks_rejected: number;
  total_completions_processed: number;
  enabled_by_org: boolean;
  approved_by_forbund: boolean;
}

export default function MentorKitSettings({ memberId }: Props) {
  const [integration, setIntegration] = useState<Integration | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [generatingWebhook, setGeneratingWebhook] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state (clientId/clientSecret er fjernet — Application Password brukes ikke lenger)
  const [wordpressUrl, setWordpressUrl] = useState('');

  // Generated webhook (vises én gang)
  const [freshWebhook, setFreshWebhook] = useState<{ url: string; secret: string } | null>(null);

  useEffect(() => { loadIntegration(); }, [memberId]);

  async function loadIntegration() {
    setLoading(true);
    try {
      const res = await authenticatedFetch(`/api/mentorkit/integrations/member/${memberId}`);
      const data = await res.json();
      if (data.success && data.integration) {
        setIntegration(data.integration);
        setWordpressUrl(data.integration.wordpress_url || '');
      } else {
        setIntegration(null);
      }
    } catch (e: any) {
      setError('Kunne ikke hente MentorKit-oppsett: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setError(null); setSuccess(null); setSaving(true);
    try {
      const body: any = { wordpress_url: wordpressUrl.trim() };

      const res = await authenticatedFetch(`/api/mentorkit/integrations/member/${memberId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.message || 'Ukjent feil');
      setSuccess('Innstillinger lagret.');
      await loadIntegration();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setError(null); setSuccess(null); setTesting(true);
    try {
      const res = await authenticatedFetch(`/api/mentorkit/integrations/member/${memberId}/test`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setSuccess(`Tilkobling OK — scorm_package-post-typen er registrert og offentlig lesbar.`);
      } else {
        setError(`Tilkoblingen feilet: ${data.message || data.error}`);
      }
      await loadIntegration();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTesting(false);
    }
  }

  async function handleGenerateWebhook() {
    if (integration?.has_webhook_secret) {
      if (!confirm('En webhook-URL og -hemmelighet er allerede satt opp. Vil du generere en NY? Da må du oppdatere MentorKit-adminen — den gamle blir ugyldig umiddelbart.')) return;
    }
    setError(null); setSuccess(null); setGeneratingWebhook(true);
    try {
      const res = await authenticatedFetch(`/api/mentorkit/integrations/member/${memberId}/generate-webhook`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Ukjent feil');
      setFreshWebhook({ url: data.webhook_url, secret: data.webhook_secret });
      await loadIntegration();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGeneratingWebhook(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Deaktivere MentorKit-integrasjonen? Webhook slutter å motta events. (Kan reaktiveres senere.)')) return;
    setSaving(true);
    try {
      await authenticatedFetch(`/api/mentorkit/integrations/member/${memberId}`, { method: 'DELETE' });
      setSuccess('MentorKit-integrasjonen er deaktivert.');
      await loadIntegration();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setSuccess(`${label} kopiert til utklippstavlen.`);
  }

  if (loading) {
    return <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> Laster MentorKit-oppsett …</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start gap-3 pb-4 border-b border-gray-200">
        <div className="p-2 bg-sky-50 rounded-lg"><Zap className="w-5 h-5 text-sky-700" /></div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">MentorKit LMS Pro <span className="ml-2 text-xs font-normal text-gray-500">Norsk Interaktiv</span></h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Push-basert integrasjon. Når en deltaker består et kurs i MentorKit, sender MentorKit
            en HMAC-signert webhook til Kompetanseforbundet, som utsteder attest automatisk.
          </p>
        </div>
      </div>

      {error && <div className="flex gap-2 items-start bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><div>{error}</div></div>}
      {success && <div className="flex gap-2 items-start bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 text-sm"><CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /><div>{success}</div></div>}

      {/* Steg 1: WordPress-URL */}
      <section className="border border-gray-200 rounded-lg p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-1">1. WordPress-URL</h3>
        <p className="text-sm text-gray-600 mb-4">
          Oppgi URL-en til MentorKit LMS Pro-installasjonen. Test-knappen verifiserer at
          <code className="mx-1 px-1.5 py-0.5 bg-gray-100 rounded text-xs">scorm_package</code>-post-typen
          er registrert og offentlig lesbar. Vi trenger ikke innlogging — mobilnummer og
          fødselsdato kommer med webhook-payloaden.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">WordPress-URL</label>
            <input
              type="url"
              value={wordpressUrl}
              onChange={e => setWordpressUrl(e.target.value)}
              placeholder="https://kunde.mentorkit.no"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !wordpressUrl.trim()}
              className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {integration ? 'Oppdater' : 'Lagre'}
            </button>
            <button
              onClick={handleTest}
              disabled={testing || !wordpressUrl.trim()}
              className="px-4 py-2 border border-sky-600 text-sky-700 rounded-md text-sm hover:bg-sky-50 disabled:opacity-50 flex items-center gap-2"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Test tilkobling
            </button>
          </div>
          {integration?.connection_tested && (
            <div className="mt-2 text-xs text-gray-600 flex items-center gap-1.5">
              {integration.connection_test_error
                ? <><AlertCircle className="w-3.5 h-3.5 text-red-600" /> Feil ved siste test: {integration.connection_test_error}</>
                : <><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> Tilkobling verifisert {integration.connection_tested_at ? new Date(integration.connection_tested_at).toLocaleString('no-NO') : ''}</>
              }
            </div>
          )}
        </div>
      </section>

      {/* Steg 2: Webhook-oppsett */}
      <section className="border border-gray-200 rounded-lg p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-1">2. Webhook-oppsett i MentorKit</h3>
        <p className="text-sm text-gray-600 mb-4">
          Generer en unik webhook-URL og signeringshemmelighet som limes inn i MentorKit-adminen
          under <em>Innstillinger → Webhooks</em>. Vi verifiserer hver innkommende event med
          HMAC-SHA256 mot denne hemmeligheten.
        </p>

        {!integration?.has_webhook_secret && !freshWebhook && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm text-amber-900 flex gap-2 items-start">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>Ingen webhook-hemmelighet er satt opp ennå. Trykk «Generer webhook» for å starte.</div>
          </div>
        )}

        {freshWebhook && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-md p-4 mb-4 space-y-3">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm font-medium text-emerald-900">
                Ny webhook opprettet. Kopier begge feltene under NÅ og lim inn i MentorKit — hemmeligheten vises ikke igjen.
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-emerald-900 mb-1">Webhook-URL</label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={freshWebhook.url}
                  className="flex-1 px-3 py-2 text-xs font-mono bg-white border border-emerald-300 rounded-md"
                />
                <button
                  onClick={() => copyToClipboard(freshWebhook.url, 'Webhook-URL')}
                  className="px-3 py-2 bg-emerald-600 text-white rounded-md text-xs hover:bg-emerald-700 flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" /> Kopier
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-emerald-900 mb-1">Signeringshemmelighet (secret)</label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={freshWebhook.secret}
                  className="flex-1 px-3 py-2 text-xs font-mono bg-white border border-emerald-300 rounded-md"
                />
                <button
                  onClick={() => copyToClipboard(freshWebhook.secret, 'Signeringshemmelighet')}
                  className="px-3 py-2 bg-emerald-600 text-white rounded-md text-xs hover:bg-emerald-700 flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" /> Kopier
                </button>
              </div>
            </div>
            <button
              onClick={() => setFreshWebhook(null)}
              className="text-xs text-emerald-800 underline hover:text-emerald-900"
            >
              Jeg har kopiert begge — lukk
            </button>
          </div>
        )}

        {integration?.webhook_url && !freshWebhook && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4 space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Aktiv webhook-URL</label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={integration.webhook_url}
                  className="flex-1 px-3 py-2 text-xs font-mono bg-white border border-gray-300 rounded-md"
                />
                <button
                  onClick={() => copyToClipboard(integration.webhook_url!, 'Webhook-URL')}
                  className="px-3 py-2 border border-gray-300 rounded-md text-xs hover:bg-gray-50 flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" /> Kopier
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-600">
              Signeringshemmeligheten er lagret kryptert og vises ikke igjen. Trykk «Generer på nytt» hvis den er tapt.
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleGenerateWebhook}
            disabled={generatingWebhook}
            className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
          >
            {generatingWebhook ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {integration?.has_webhook_secret ? 'Generer på nytt' : 'Generer webhook'}
          </button>
          <a
            href="https://help.mentorkit.com/mentorkit-lms/developer/webhooks"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-sm text-sky-700 hover:underline flex items-center gap-1.5"
          >
            <ExternalLink className="w-3.5 h-3.5" /> MentorKit sin webhook-dokumentasjon
          </a>
        </div>
      </section>

      {/* Steg 3: Status */}
      {integration && (
        <section className="border border-gray-200 rounded-lg p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-3">3. Driftsstatus</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile label="Mottatt totalt" value={integration.total_webhooks_received} />
            <StatTile label="Akseptert" value={integration.total_webhooks_accepted} color="text-green-700" />
            <StatTile label="Avvist" value={integration.total_webhooks_rejected} color="text-red-700" />
            <StatTile label="Fullføringer" value={integration.total_completions_processed} color="text-sky-700" />
          </div>
          {integration.last_webhook_at && (
            <div className="mt-3 text-xs text-gray-600">
              Siste webhook: {new Date(integration.last_webhook_at).toLocaleString('no-NO')}
              {integration.last_webhook_status && <> · Status: <b>{integration.last_webhook_status}</b></>}
              {integration.last_webhook_error && <> · Feil: {integration.last_webhook_error}</>}
            </div>
          )}
          {!integration.last_webhook_at && (
            <div className="mt-3 text-xs text-gray-500">Ingen webhooks mottatt ennå.</div>
          )}
        </section>
      )}

      {/* Deaktiver */}
      {integration && integration.enabled_by_org && (
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={handleDelete}
            className="text-sm text-red-700 hover:text-red-900 flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Deaktiver MentorKit-integrasjonen
          </button>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, color = 'text-gray-900' }: { label: string; value: number | null; color?: string }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
      <div className={`text-xl font-semibold tabular-nums ${color}`}>{value ?? 0}</div>
      <div className="text-xs text-gray-600 mt-0.5">{label}</div>
    </div>
  );
}
